/**
 * Action Executor — dispatches parsed actions to the Excel Context Service.
 */

import {SpreadsheetAction} from "./actionParser";
import {actionRegistry} from "./actionRegistry";

/**
 * How an action ended.
 *
 * `declined` is deliberately not a `failure`: a user refusing a destructive
 * script is the confirmation rail doing its job, and folding the two together
 * both inflates the failure rate and hides how often the rail actually fires
 * (issue #47).
 */
export type ActionOutcome = "success" | "failure" | "declined";

/**
 * The glyph each outcome is written with, in one place so the debug log, the
 * follow-up prompt, and the task pane can't drift into disagreeing about what
 * a decline looks like — a decline rendered as ✗ anywhere is the very
 * confusion this type exists to remove.
 */
export const OUTCOME_MARKERS: Record<ActionOutcome, string> = {
  success: "✓",
  failure: "✗",
  declined: "⊘",
};

/**
 * Who asked the confirmation question — the model, via its own
 * `confirmMessage`, or `detectDestructiveScript`'s backstop regex.
 *
 * Worth keeping apart because a high decline rate means opposite things in
 * each case: the model proposing damage is an agent problem, while the
 * heuristic over-firing means we're nagging people about safe scripts and
 * training them to click through the ones that matter.
 */
export type ConfirmationSource = "model" | "heuristic";

/** A confirmation prompt that was actually shown, and what the user did with it. */
export interface ConfirmationRecord {
  source: ConfirmationSource;
  outcome: "accepted" | "declined";
}

export interface ActionResult {
  outcome: ActionOutcome;
  /** Readable one-liner, shown to the user and sent to the model. */
  message: string;
  /**
   * Data the model needs but the user doesn't — currently a script's
   * returned value, which goes into the next iteration's prompt while the
   * user sees only `message`.
   */
  detail?: string;
  /** Bounded diagnostic code for failed actions; the full message is span-only. */
  errorCode?: ActionErrorCode;
  retryable?: boolean;
  /**
   * Set only when a confirmation prompt was shown. An accepted confirmation
   * is recorded whatever the action then did, so the accept rate isn't
   * conflated with the success rate of what followed.
   */
  confirmation?: ConfirmationRecord;
}

export type ActionErrorCode =
  | "ItemNotFound"
  | "InvalidArgument"
  | "AccessDenied"
  | "script_timeout"
  | "script_syntax"
  | "script_validation"
  | "script_checker_unavailable"
  | "unknown";

const ACTION_ERROR_CODES: readonly ActionErrorCode[] = [
  "ItemNotFound",
  "InvalidArgument",
  "AccessDenied",
  "script_timeout",
  "script_syntax",
  "script_validation",
  "script_checker_unavailable",
  "unknown",
];

export interface ActionErrorDetails {
  code: ActionErrorCode;
  message: string;
  detail?: string;
}

export function getActionErrorDetails(error: unknown): ActionErrorDetails {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "object" &&
          error !== null &&
          typeof (error as {message?: unknown}).message === "string"
        ? (error as {message: string}).message
        : String(error);
  const suppliedCode =
    typeof error === "object" && error !== null
      ? (error as {code?: unknown}).code
      : undefined;
  const code = ACTION_ERROR_CODES.includes(suppliedCode as ActionErrorCode)
    ? (suppliedCode as ActionErrorCode)
    : message.startsWith("Script timed out")
      ? "script_timeout"
      : message.startsWith("Script has a syntax error")
        ? "script_syntax"
        : "unknown";
  const detail =
    code === "script_validation" && typeof error === "object" && error !== null
      ? (error as {detail?: unknown}).detail
      : undefined;
  return {code, message, ...(typeof detail === "string" ? {detail} : {})};
}

/** Asks the user to confirm a plain-English message before a destructive script runs. */
export type ConfirmFn = (message: string) => boolean | Promise<boolean>;

const defaultConfirm: ConfirmFn = (message) =>
  typeof window !== "undefined" && typeof window.confirm === "function"
    ? window.confirm(message)
    : true;

const SHEET_DELETE_RE =
  /worksheets\s*\.\s*getItem\(\s*["']([^"']+)["']\s*\)\s*\.\s*delete\s*\(/;
const ACTIVE_SHEET_DELETE_RE = /getActiveWorksheet\(\s*\)\s*\.\s*delete\s*\(/;
const RANGE_REF_RE = /getRange\(\s*["']([^"']+)["']\s*\)/;
const CLEAR_RE = /\.\s*clear(?:Contents|Formats)?\s*\(/;
const VALUES_ASSIGN_RE = /\.\s*values\s*=[^=]/;

/**
 * Detects common destructive Office.js patterns in generated script code, as
 * a backstop for scripts the model doesn't flag with its own confirmMessage.
 * Returns a plain-English confirmation question (never the code itself), or
 * undefined if the script doesn't match a known destructive pattern.
 */
export function detectDestructiveScript(code: string): string | undefined {
  const sheetDelete = code.match(SHEET_DELETE_RE);
  if (sheetDelete) {
    return `This will delete the sheet "${sheetDelete[1]}". Continue?`;
  }
  if (ACTIVE_SHEET_DELETE_RE.test(code)) {
    return "This will delete the active sheet. Continue?";
  }

  const rangeRef = code.match(RANGE_REF_RE)?.[1];

  if (CLEAR_RE.test(code)) {
    return rangeRef
      ? `This will clear the contents of range ${rangeRef}. Continue?`
      : "This will clear the contents of the affected cells. Continue?";
  }

  if (VALUES_ASSIGN_RE.test(code)) {
    return rangeRef
      ? `This will overwrite the values in range ${rangeRef}. Continue?`
      : "This will overwrite existing cell values. Continue?";
  }

  return undefined;
}

export async function executeAction(
  action: SpreadsheetAction,
  confirm: ConfirmFn = defaultConfirm,
): Promise<ActionResult> {
  let confirmation: ConfirmationRecord | undefined;

  if (action.type === "run_script") {
    const source: ConfirmationSource = action.confirmMessage
      ? "model"
      : "heuristic";
    const confirmMessage =
      action.confirmMessage ?? detectDestructiveScript(action.code);
    if (confirmMessage) {
      const proceed = await confirm(confirmMessage);
      // Recorded before the early return so an accepted prompt is counted on
      // every path out of here, not just the one where the script works.
      confirmation = {source, outcome: proceed ? "accepted" : "declined"};
      if (!proceed) {
        return {
          outcome: "declined",
          message: `Script cancelled by user at the confirmation prompt: "${confirmMessage}"`,
          confirmation,
        };
      }
    }
  }

  try {
    // The registry's mapped type guarantees execute/describe agree with
    // `action`'s shape for its `type`, but a plain index access loses that
    // link — narrow with one cast at this single seam.
    const definition = actionRegistry[action.type] as {
      execute(a: SpreadsheetAction): Promise<unknown>;
      describe(a: SpreadsheetAction, result: unknown): string;
      detail?(a: SpreadsheetAction, result: unknown): string | undefined;
    };
    const result = await definition.execute(action);
    return {
      outcome: "success",
      message: definition.describe(action, result),
      detail: definition.detail?.(action, result),
      confirmation,
    };
  } catch (error) {
    const details = getActionErrorDetails(error);
    return {
      outcome: "failure",
      message: `Action failed: ${details.message}`,
      detail: details.detail,
      errorCode: details.code,
      confirmation,
    };
  }
}

export async function executeActions(
  actions: SpreadsheetAction[],
  confirm: ConfirmFn = defaultConfirm,
): Promise<ActionResult[]> {
  const results: ActionResult[] = [];
  for (const action of actions) {
    results.push(await executeAction(action, confirm));
  }
  return results;
}
